import { useEffect, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";

import { SESSION_EXPIRED } from "../../api/client.ts";
import { useSession } from "../../api/hooks.ts";
import { InactivityToast } from "./InactivityToast.tsx";
import { OwnerProvider } from "./OwnerProvider.tsx";

/**
 * SPEC 19.14: un 401 en CUALQUIER llamada devuelve al inicio de sesion, y se
 * resuelve una sola vez, aqui y en `client.ts`. Ninguna pantalla lo repite.
 *
 * Se guarda la ruta actual y se vuelve a ella despues de entrar: si no, cerrar
 * sesion por caducidad en medio de un cierre de operacion te dejaria en el panel
 * sin saber donde estabas.
 */
export function RequireSession(): ReactNode {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onExpired = (): void => {
      navigate("/login", { replace: true, state: { from: location.pathname } });
    };

    window.addEventListener(SESSION_EXPIRED, onExpired);

    return () => window.removeEventListener(SESSION_EXPIRED, onExpired);
  }, [navigate, location.pathname]);

  if (session.isPending) {
    return (
      <div className="splash">
        <span className="spinner dark" />
        <p>Comprobando la sesion…</p>
      </div>
    );
  }

  if (session.isError) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  /* El aviso de inactividad vive aqui y no en `main.tsx` porque solo tiene
     sentido con sesion abierta: en la pantalla de entrar no hay nada que
     caducar. Y el dueno de la bitacora que se mira tambien: necesita saber
     quien es "yo", y eso solo se sabe con la sesion resuelta. */
  return (
    <OwnerProvider>
      <Outlet />
      <InactivityToast />
    </OwnerProvider>
  );
}
